import { X } from "lucide-react"
import { tagColor } from "@/lib/tag-styles"
import { tagIcon } from "@/lib/tag-icons"
import { cn } from "@/lib/utils"

// One tag as it travels through props — id + name + color + icon.
export type Tag = {
  id: number
  name: string
  color: string
  icon: string
}

type Props = {
  tag: Tag
  // When provided, renders a trailing "×" that calls this on click — used in
  // pickers where a selected tag can be removed.
  onRemove?: () => void
  className?: string
}

// A small label badge: faded background, full-color text + icon, both driven
// by the tag's color. The icon inherits `currentColor` so it matches the text.
// `!text-current` forces that inheritance even inside containers that try to
// recolor nested SVGs (e.g. DropdownMenuItem's `[&_svg]:text-ink-muted`).
export function TagBadge({ tag, onRemove, className }: Props) {
  const color = tagColor(tag.color)
  const Icon = tagIcon(tag.icon)
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-xs font-medium leading-none",
        color.badge,
        className,
      )}
    >
      <Icon className="h-3 w-3 shrink-0 !text-current" />
      <span className="truncate">{tag.name}</span>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onRemove()
          }}
          aria-label={`Remove ${tag.name}`}
          className="-mr-0.5 ml-0.5 inline-flex items-center justify-center rounded-full hover:opacity-70"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  )
}
